// =============================================================================
// CANVAS DATA - Single Source of Truth
// =============================================================================
// All mock data for the canvas page is centralized here.
// Components import from this file to ensure consistency.
// Node definitions: nodeLibrary.json
// Workflow schema: workflowSchema.json
// =============================================================================
import workflowSchema from './workflowSchema.json';

// -----------------------------------------------------------------------------
// FILE SYSTEM
// -----------------------------------------------------------------------------
export const fileSystem = [
  {
    id: 'claude-config',
    name: '.claude',
    type: 'folder',
    isAgentConfig: true,
    agentType: 'claude',
    children: [
      {
        id: 'claude-settings',
        name: 'settings.json',
        type: 'file',
        isConfig: true,
        lines: 24,
        description: 'Claude Code settings'
      },
      {
        id: 'claude-hooks',
        name: 'hooks',
        type: 'folder',
        isConfig: true,
        children: [
          { id: 'claude-hook-precommit', name: 'pre-commit.sh', type: 'file', isConfig: true, lines: 18, description: 'Pre-commit validation hook' },
          { id: 'claude-hook-postpush', name: 'post-push.sh', type: 'file', isConfig: true, lines: 12, description: 'Post-push notification hook' },
          { id: 'claude-hook-onresponse', name: 'on-response.js', type: 'file', isConfig: true, lines: 34, description: 'Response processing hook' }
        ]
      },
      {
        id: 'claude-skills',
        name: 'skills',
        type: 'folder',
        isConfig: true,
        children: [
          { id: 'claude-skill-deploy', name: 'deploy.md', type: 'file', isConfig: true, lines: 45, description: 'Custom deploy skill' },
          { id: 'claude-skill-review', name: 'code-review.md', type: 'file', isConfig: true, lines: 62, description: 'PR review skill' }
        ]
      },
    ]
  },
  {
    id: 'cursor-config',
    name: '.cursor',
    type: 'folder',
    isAgentConfig: true,
    agentType: 'cursor',
    children: [
      {
        id: 'cursor-rules',
        name: 'rules',
        type: 'folder',
        isConfig: true,
        children: [
          { id: 'cursor-rule-global', name: 'global.mdc', type: 'file', isConfig: true, lines: 56, description: 'Global coding rules' },
          { id: 'cursor-rule-react', name: 'react.mdc', type: 'file', isConfig: true, lines: 42, description: 'React-specific rules' },
          { id: 'cursor-rule-testing', name: 'testing.mdc', type: 'file', isConfig: true, lines: 31, description: 'Testing conventions' }
        ]
      },
      {
        id: 'cursor-tools',
        name: 'tools',
        type: 'folder',
        isConfig: true,
        children: [
          { id: 'cursor-tool-lint', name: 'lint-fix.js', type: 'file', isConfig: true, lines: 28, description: 'Auto-fix linting tool' },
          { id: 'cursor-tool-migrate', name: 'db-migrate.js', type: 'file', isConfig: true, lines: 67, description: 'Database migration tool' }
        ]
      },
      {
        id: 'cursor-settings',
        name: 'settings.json',
        type: 'file',
        isConfig: true,
        lines: 19,
        description: 'Cursor AI settings'
      }
    ]
  },
  {
    id: '1',
    name: 'src',
    type: 'folder',
    children: [
      {
        id: '1-1',
        name: 'api',
        type: 'folder',
        children: [
          {
            id: '1-1-1',
            name: 'routes.js',
            type: 'file',
            lines: 245,
            classes: [],
            functions: [
              { name: 'registerRoutes', lines: 45, startLine: 12 },
              { name: 'authMiddleware', lines: 28, startLine: 62 },
              { name: 'validateRequest', lines: 32, startLine: 95 },
              { name: 'handleError', lines: 18, startLine: 132 }
            ],
            imports: ['1-1-2', '1-2-1', '1-2-2'],
            externalCalls: ['ext-postgres-1']
          },
          {
            id: '1-1-2',
            name: 'middleware.js',
            type: 'file',
            lines: 156,
            classes: [],
            functions: [
              { name: 'corsHandler', lines: 22, startLine: 8 },
              { name: 'rateLimiter', lines: 45, startLine: 35 },
              { name: 'requestLogger', lines: 18, startLine: 85 }
            ],
            imports: ['1-2-2', '1-3-1'],
            externalCalls: []
          },
        ]
      },
      {
        id: '1-2',
        name: 'services',
        type: 'folder',
        children: [
          {
            id: '1-2-1',
            name: 'userService.js',
            type: 'file',
            lines: 312,
            classes: [
              { name: 'UserService', lines: 280, startLine: 15 }
            ],
            functions: [
              { name: 'createUser', lines: 42, startLine: 25 },
              { name: 'updateUser', lines: 38, startLine: 72 },
              { name: 'deleteUser', lines: 24, startLine: 115 },
              { name: 'findUserById', lines: 18, startLine: 144 },
              { name: 'validateEmail', lines: 12, startLine: 167 }
            ],
            imports: ['2-1', '1-3-1'],
            externalCalls: ['ext-postgres-1']
          },
          {
            id: '1-2-2',
            name: 'authService.js',
            type: 'file',
            lines: 198,
            classes: [
              { name: 'AuthService', lines: 165, startLine: 18 }
            ],
            functions: [
              { name: 'login', lines: 35, startLine: 28 },
              { name: 'logout', lines: 15, startLine: 68 },
              { name: 'refreshToken', lines: 28, startLine: 88 },
              { name: 'validateToken', lines: 22, startLine: 121 }
            ],
            imports: ['2-2', '1-3-2'],
            externalCalls: []
          },
          {
            id: '1-2-3',
            name: 'llmService.js',
            type: 'file',
            lines: 428,
            classes: [
              { name: 'LLMService', lines: 380, startLine: 22 }
            ],
            functions: [
              { name: 'chat', lines: 65, startLine: 35 },
              { name: 'embed', lines: 42, startLine: 105 },
              { name: 'streamResponse', lines: 88, startLine: 152 },
              { name: 'handleToolUse', lines: 54, startLine: 245 }
            ],
            imports: ['2-2'],
            externalCalls: ['ext-openai-1', 'ext-openai-2']
          },
        ]
      },
      {
        id: '1-3',
        name: 'utils',
        type: 'folder',
        children: [
          {
            id: '1-3-1',
            name: 'helpers.js',
            type: 'file',
            lines: 124,
            classes: [],
            functions: [
              { name: 'formatDate', lines: 12, startLine: 5 },
              { name: 'parseJSON', lines: 18, startLine: 22 },
              { name: 'debounce', lines: 15, startLine: 45 },
              { name: 'throttle', lines: 16, startLine: 65 }
            ],
            imports: [],
            externalCalls: []
          },
          {
            id: '1-3-2',
            name: 'constants.js',
            type: 'file',
            lines: 45,
            classes: [],
            functions: [],
            imports: [],
            externalCalls: []
          },
        ]
      }
    ]
  },
  {
    id: '2',
    name: 'config',
    type: 'folder',
    children: [
      {
        id: '2-1',
        name: 'database.js',
        type: 'file',
        lines: 87,
        classes: [],
        functions: [
          { name: 'connect', lines: 25, startLine: 8 },
          { name: 'disconnect', lines: 12, startLine: 38 },
          { name: 'query', lines: 32, startLine: 55 }
        ],
        imports: [],
        externalCalls: ['ext-postgres-1', 'ext-postgres-2']
      },
      {
        id: '2-2',
        name: 'api-keys.js',
        type: 'file',
        lines: 34,
        classes: [],
        functions: [
          { name: 'getKey', lines: 8, startLine: 12 },
          { name: 'rotateKey', lines: 14, startLine: 25 }
        ],
        imports: [],
        externalCalls: ['ext-openai-1', 'ext-stripe-1']
      },
    ]
  },
  {
    id: '3',
    name: 'package.json',
    type: 'file',
    lines: 42,
    classes: [],
    functions: [],
    imports: [],
    externalCalls: []
  }
];

// -----------------------------------------------------------------------------
// EXTERNAL SERVICES
// -----------------------------------------------------------------------------
export const externalServices = [
  {
    id: 'ext-openai',
    name: 'openai',
    type: 'folder',
    isExternal: true,
    children: [
      { id: 'ext-openai-1', name: 'chat/completions', type: 'endpoint', isExternal: true, description: 'GPT-4 chat API' },
      { id: 'ext-openai-2', name: 'embeddings', type: 'endpoint', isExternal: true, description: 'Text embeddings API' },
    ]
  },
  {
    id: 'ext-stripe',
    name: 'stripe',
    type: 'folder',
    isExternal: true,
    children: [
      { id: 'ext-stripe-1', name: 'customers', type: 'endpoint', isExternal: true, description: 'Customer management' },
      { id: 'ext-stripe-2', name: 'payments', type: 'endpoint', isExternal: true, description: 'Payment processing' },
    ]
  },
  {
    id: 'ext-postgres',
    name: 'postgres',
    type: 'folder',
    isExternal: true,
    children: [
      { id: 'ext-postgres-1', name: 'users', type: 'endpoint', isExternal: true, description: 'Users table' },
      { id: 'ext-postgres-2', name: 'sessions', type: 'endpoint', isExternal: true, description: 'Sessions table' },
    ]
  }
];

// Helper to find a file by ID in the file system
export function findFileById(id, items = fileSystem) {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findFileById(id, item.children);
      if (found) return found;
    }
  }
  // Also search external services
  for (const service of externalServices) {
    if (service.id === id) return service;
    if (service.children) {
      const found = service.children.find(c => c.id === id);
      if (found) return found;
    }
  }
  return null;
}

// -----------------------------------------------------------------------------
// CONVERSATIONS (Chat history)
// -----------------------------------------------------------------------------
// Conversations are linked to workflows via workflowId
// agentType indicates which tool config folder to highlight (.claude or .cursor)
export const conversations = [
  {
    id: 'conv-1',
    workflowId: 'workflow-1', // Links to "Add Feature" workflow
    userMessage: {
      content: 'Update the user service to add email validation',
      time: '10:32 AM'
    },
    agentResponse: {
      content: "I'll update the user service to add email validation. Let me analyze the codebase structure first.\n\nI found the user service at `src/services/userService.js`. I'll add validation that:\n1. Checks email format with regex\n2. Validates domain against blocklist\n3. Returns helpful error messages",
      time: '10:32 AM',
      agentType: 'claude', // Highlights .claude folder when clicked
      configFileIds: ['claude-config', 'claude-hooks', 'claude-hook-onresponse'], // Config files used
      pushNumber: 47,
      fileChanges: [
        {
          fileId: '1-2-1',
          file: 'src/services/userService.js',
          type: 'modified',
          additions: 24,
          deletions: 2,
          diffs: [
            {
              oldLine: "  validateEmail(email) {",
              newLine: "  validateEmail(email) {\n    const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;",
              lineNumber: 167
            },
            {
              oldLine: "    return true;",
              newLine: "    if (!emailRegex.test(email)) {\n      throw new Error('Invalid email format');\n    }\n    return true;",
              lineNumber: 172
            }
          ]
        },
        {
          fileId: '1-3-1',
          file: 'src/utils/helpers.js',
          type: 'modified',
          additions: 12,
          deletions: 0,
          diffs: [
            {
              oldLine: "function parseJSON(str) {",
              newLine: "function isValidDomain(domain) {\n  const blocked = ['tempmail.com', 'throwaway.com'];\n  return !blocked.includes(domain);\n}\n\nfunction parseJSON(str) {",
              lineNumber: 22
            }
          ]
        }
      ],
      logs: [
        { time: '10:32:01', text: 'Reading src/services/userService.js' },
        { time: '10:32:02', text: 'Found validateEmail function at line 167' },
        { time: '10:32:03', text: 'Loaded hook: .claude/hooks/on-response.js' },
        { time: '10:32:05', text: 'Lint passed with 0 warnings' },
        { time: '10:32:06', text: 'Tests: 12/12 passed' }
      ]
    }
  },
  {
    id: 'conv-2',
    workflowId: 'workflow-2', // Links to "Bug Fix" workflow
    userMessage: {
      content: 'Fix the race condition in the data fetching logic',
      time: '2:15 PM'
    },
    agentResponse: {
      content: "I've identified the race condition in the data fetching logic. The issue occurs when multiple requests are fired simultaneously without proper cancellation.\n\nApplying fix:\n1. Added AbortController for request cancellation\n2. Implemented request deduplication\n3. Added proper cleanup in useEffect",
      time: '2:15 PM',
      agentType: 'cursor', // Highlights .cursor folder when clicked
      configFileIds: ['cursor-config', 'cursor-rules', 'cursor-rule-react'], // Config files used
      pushNumber: 23,
      fileChanges: [
        {
          fileId: '1-2-1',
          file: 'src/services/userService.js',
          type: 'modified',
          additions: 18,
          deletions: 5,
          diffs: [
            {
              oldLine: "  async fetchUser(id) {",
              newLine: "  async fetchUser(id, signal) {\n    // Abort controller support per .cursor/rules/react.mdc",
              lineNumber: 45
            }
          ]
        }
      ],
      logs: [
        { time: '2:15:01', text: 'Analyzing codebase with .cursor/rules/react.mdc' },
        { time: '2:15:02', text: 'Found race condition in fetchUser' },
        { time: '2:15:04', text: 'Applied fix using cursor tool: lint-fix.js' },
        { time: '2:15:05', text: 'Tests: 8/8 passed' }
      ]
    }
  },
  {
    id: 'conv-3',
    workflowId: 'workflow-1', // Also "Add Feature"
    userMessage: {
      content: 'Add rate limiting to the API endpoints',
      time: '4:45 PM'
    },
    agentResponse: {
      content: "I'll implement rate limiting for the API endpoints. Based on your `.claude/skills/deploy.md` configuration, I'll use a token bucket algorithm.\n\nChanges made:\n1. Added rateLimiter middleware\n2. Configured limits per endpoint\n3. Added Redis cache for distributed limiting",
      time: '4:45 PM',
      agentType: 'claude',
      configFileIds: ['claude-config', 'claude-skills', 'claude-skill-deploy'],
      pushNumber: 48,
      fileChanges: [
        {
          fileId: '1-1-2',
          file: 'src/api/middleware.js',
          type: 'modified',
          additions: 45,
          deletions: 0,
          diffs: [
            {
              oldLine: "function rateLimiter(req, res, next) {",
              newLine: "function rateLimiter(req, res, next) {\n  // Token bucket per .claude/skills/deploy.md\n  const bucket = getBucket(req.ip);",
              lineNumber: 35
            }
          ]
        }
      ],
      logs: [
        { time: '4:45:01', text: 'Loading skill: .claude/skills/deploy.md' },
        { time: '4:45:02', text: 'Analyzing middleware.js' },
        { time: '4:45:04', text: 'Running pre-commit hook' },
        { time: '4:45:06', text: 'Tests: 15/15 passed' }
      ]
    }
  }
];

// Helper to get conversations for a specific workflow
export function getConversationsByWorkflow(workflowId) {
  return conversations.filter(c => c.workflowId === workflowId);
}

// -----------------------------------------------------------------------------
// AGENT CONFIGURATIONS (Tool-Agnostic Support)
// -----------------------------------------------------------------------------
// Maps agent types to their config folder IDs for highlighting
export const agentConfigs = {
  claude: {
    id: 'claude-config',
    name: '.claude',
    color: '#D97706', // amber/orange for Claude
    features: ['hooks', 'skills'],
    description: 'Claude Code configuration'
  },
  cursor: {
    id: 'cursor-config',
    name: '.cursor',
    color: '#3B82F6', // blue for Cursor
    features: ['rules', 'tools'],
    description: 'Cursor AI configuration'
  }
};

// Helper to get agent config by type
export function getAgentConfig(agentType) {
  return agentConfigs[agentType] || null;
}

// Helper to get the config folder ID for an agent type
export function getAgentConfigFolderId(agentType) {
  const config = agentConfigs[agentType];
  return config ? config.id : null;
}

// Helper to get all config file IDs for an agent (for highlighting)
export function getAgentConfigFileIds(agentType) {
  const configFolder = fileSystem.find(f => f.agentType === agentType);
  if (!configFolder) return [];

  const collectIds = (item) => {
    const ids = [item.id];
    if (item.children) {
      item.children.forEach(child => {
        ids.push(...collectIds(child));
      });
    }
    return ids;
  };

  return collectIds(configFolder);
}

// Helper to determine which agent config files are relevant to a response
export function getResponseConfigHighlights(conversationId) {
  const conv = conversations.find(c => c.id === conversationId);
  if (!conv || !conv.agentResponse) return null;

  return {
    agentType: conv.agentResponse.agentType,
    configFolderId: getAgentConfigFolderId(conv.agentResponse.agentType),
    configFileIds: conv.agentResponse.configFileIds || [],
    agentConfig: getAgentConfig(conv.agentResponse.agentType)
  };
}

// -----------------------------------------------------------------------------
// WORKFLOWS
// -----------------------------------------------------------------------------
// Mutable array - workflows can be added via saveWorkflow()
export const workflows = [];

// Save/add a workflow to the list
export function saveWorkflow(workflowData) {
  const responseStep = workflowData.steps?.find(s => s.isEnd || s.nodeId === 'response');
  const outputMode = responseStep?.config?.outputMode || 'claude';
  const agentType = outputMode === 'cursor' ? 'cursor' : 'claude';

  const mockPushHistory = workflowSchema.mockPushHistory;
  const defaults = workflowSchema.defaultWorkflow;

  const totalTokens = mockPushHistory.reduce((sum, p) => sum + p.tokens, 0);
  const revertedPushes = mockPushHistory.filter(p => p.status === 'reverted').length;
  const revertsPerToken = revertedPushes / (totalTokens / 1000);

  const now = new Date().toISOString();

  const newWorkflow = {
    id: `workflow-${Date.now()}`,
    name: workflowData.name || defaults.name,
    description: workflowData.description || defaults.description,
    lastUpdated: now,
    created: workflowData.created || now,
    tags: workflowData.tags || defaults.tags,
    inChat: defaults.inChat,
    agentType,
    agentConfigId: agentType === 'claude' ? 'claude-config' : 'cursor-config',
    currentVersion: defaults.currentVersion,
    steps: workflowData.steps || [],

    evaluation: {
      currentScore: revertsPerToken,
      revertsPerToken,
      totalTokens,
      trend: 'improving',
      totalLifetimePushes: mockPushHistory.length,
      versions: [
        {
          id: 'v1',
          number: 'v1',
          status: 'active',
          score: revertsPerToken,
          revertsPerToken,
          previousScore: null,
          pushes: mockPushHistory.length,
          cleanPushes: mockPushHistory.filter(p => p.status === 'merged').length,
          revertedPushes,
          totalTokens,
          pushHistory: mockPushHistory
        }
      ]
    }
  };

  workflows.push(newWorkflow);
  return newWorkflow;
}

// Helper to get a workflow by ID
export function getWorkflowById(id) {
  return workflows.find(w => w.id === id);
}

// Helper to get all workflows for the list view
export function getWorkflowsList() {
  return workflows.map(w => ({
    id: w.id,
    name: w.name,
    description: w.description,
    lastUpdated: w.lastUpdated,
    created: w.created,
    tags: w.tags,
    currentVersion: w.currentVersion,
    stepCount: w.steps.length,
    agentType: w.agentType,
    agentConfigId: w.agentConfigId
  }));
}

// Helper to get evaluation data for all workflows
export function getEvaluationData() {
  return workflows.map(w => ({
    id: w.id,
    name: w.name,
    currentVersion: w.currentVersion,
    ...w.evaluation
  }));
}
